(() => {
  const contextElement = document.getElementById('waisa-webmcp-context');
  const modelContext = document.modelContext || navigator.modelContext;

  if (!contextElement || !modelContext?.registerTool) return;

  let context;
  try {
    context = JSON.parse(contextElement.textContent);
  } catch {
    return;
  }

  const HARDCODED_RETAIL_CANS = [
    { title: 'Vibrant Peach', url: '/products/vibrant-peach' },
    { title: 'Golden Pineapple', url: '/products/golden-pineapple' },
    { title: 'Wildberry Mint', url: '/products/wildberry-mint' },
  ];

  const retailCans = () => {
    const fromContext = Array.isArray(context.retail_cans) ? context.retail_cans : [];
    const source = fromContext.length > 0 ? fromContext : HARDCODED_RETAIL_CANS;
    return source
      .filter((product) => product?.title)
      .map((product) => ({
        title: product.title,
        url: product.url || '',
      }));
  };

  const PROVINCE_NAMES = {
    ab: 'alberta',
    bc: 'british columbia',
    mb: 'manitoba',
    nb: 'new brunswick',
    nl: 'newfoundland and labrador',
    ns: 'nova scotia',
    nt: 'northwest territories',
    nu: 'nunavut',
    on: 'ontario',
    pe: 'prince edward island',
    qc: 'quebec',
    sk: 'saskatchewan',
    yt: 'yukon',
  };
  const PROVINCE_CODES_BY_NAME = Object.fromEntries(
    Object.entries(PROVINCE_NAMES).map(([code, name]) => [name, code])
  );
  const CITY_ALIASES = { 'north york': 'toronto' };

  const normalize = (value) =>
    String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const compactPostal = (value) => normalize(value).replace(/[^a-z0-9]/g, '');

  const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const containsWholeWord = (haystack, needle) => {
    if (!haystack || !needle) return false;
    return new RegExp(`(^|[^a-z0-9])${escapeRegex(needle)}([^a-z0-9]|$)`).test(haystack);
  };

  const provinceCodeFrom = (value) => {
    const normalized = normalize(value);
    if (PROVINCE_NAMES[normalized]) return normalized;
    if (PROVINCE_CODES_BY_NAME[normalized]) return PROVINCE_CODES_BY_NAME[normalized];
    return null;
  };

  const parsePostal = (value) => {
    const compact = compactPostal(value);
    if (/^[abceghjklmnprstvxy]\d[a-z]\d[a-z]\d$/.test(compact)) {
      return { full: compact, fsa: compact.slice(0, 3) };
    }
    if (/^[abceghjklmnprstvxy]\d[a-z]$/.test(compact)) {
      return { full: null, fsa: compact };
    }
    return null;
  };

  // Wider postal areas to try when no stockist has the exact code: the FSA (M5V),
  // the postal district (M5), then the postal region (M).
  const widerPostalAreas = (postal) => {
    const areas = postal.full ? [postal.fsa] : [];
    return [...areas, postal.fsa.slice(0, 2), postal.fsa.slice(0, 1)];
  };

  const formatPostal = (value) => {
    const compact = compactPostal(value).toUpperCase();
    return compact.length === 6 ? `${compact.slice(0, 3)} ${compact.slice(3)}` : compact;
  };

  const PLACE_PREFIX = /^(downtown|greater|city of)\s+/;

  const cleanPlace = (place) => {
    if (!place) return place;
    const stripped = place.replace(PLACE_PREFIX, '').trim();
    return stripped || place;
  };

  const stripTrailingCountry = (query) => {
    const match = query.match(/^(.*?)(?:,\s*|\s+)(canada|ca)$/);
    if (!match) return { query, country: undefined };
    const stripped = match[1].trim().replace(/,+$/g, '').trim();
    if (!stripped) return { query, country: undefined };
    return { query: stripped, country: 'canada' };
  };

  const withCountry = (parsed, country) => {
    if (!parsed || !country) return parsed;
    return { ...parsed, country };
  };

  const parseLocationQuery = (query) => {
    if (!query) return null;

    const stripped = stripTrailingCountry(query);
    const working = stripped.query;
    const country = stripped.country;

    const postalOnly = parsePostal(working);
    if (postalOnly) return withCountry({ postal: postalOnly }, country);

    const commaParts = working
      .split(',')
      .map((part) => normalize(part))
      .filter(Boolean);
    if (commaParts.length === 2) {
      const province = provinceCodeFrom(commaParts[1]);
      const postal = parsePostal(commaParts[1]);
      if (province) {
        return withCountry({ place: cleanPlace(commaParts[0]), province }, country);
      }
      if (postal) {
        return withCountry({ place: cleanPlace(commaParts[0]), postal }, country);
      }
    }

    const tokens = working.split(/\s+/).filter(Boolean);
    if (tokens.length >= 2) {
      const province = provinceCodeFrom(tokens[tokens.length - 1]);
      if (province) {
        return withCountry({ place: cleanPlace(tokens.slice(0, -1).join(' ')), province }, country);
      }
    }

    const province = provinceCodeFrom(working);
    if (province && (PROVINCE_NAMES[working] || PROVINCE_CODES_BY_NAME[working])) {
      return withCountry({ province }, country);
    }

    if (working === 'canada' || working === 'ca') return { country: 'canada' };
    return withCountry({ place: cleanPlace(working) }, country);
  };

  const stockistMatchesLocation = (stockist, parsedQuery, cityNames) => {
    if (!parsedQuery) return true;

    const city = normalize(stockist.city);
    const aliasedCity = CITY_ALIASES[city] || city;
    const state = provinceCodeFrom(stockist.state) || normalize(stockist.state);
    const zip = compactPostal(stockist.zip);
    const name = normalize(stockist.name);
    const address = normalize(stockist.address);
    const country = normalize(stockist.country || 'canada');

    if (parsedQuery.province && state !== parsedQuery.province) return false;
    if (parsedQuery.country && country !== parsedQuery.country) return false;
    if (parsedQuery.postal) {
      if (parsedQuery.place) {
        const cityMatchesPostalPlace =
          city === parsedQuery.place ||
          aliasedCity === parsedQuery.place ||
          containsWholeWord(city, parsedQuery.place);
        if (!cityMatchesPostalPlace) return false;
      }
      if (parsedQuery.postal.full) return zip === parsedQuery.postal.full;
      return zip.startsWith(parsedQuery.postal.fsa);
    }
    if (!parsedQuery.place) return true;

    const place = parsedQuery.place;
    if (city === place || aliasedCity === place) return true;
    if (cityNames?.has(place)) return false;
    if (place.length >= 3 && (city.startsWith(place) || aliasedCity.startsWith(place))) {
      return true;
    }
    if (
      place.length >= 3 &&
      (containsWholeWord(city, place) || containsWholeWord(aliasedCity, place))
    ) {
      return true;
    }
    if (place.length >= 3 && name.includes(place)) return true;
    if ((place.includes(' ') || /\d/.test(place)) && address.includes(place)) return true;
    return false;
  };

  const normalizeStreet = (value) =>
    normalize(value)
      .replace(/[.]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\b(street)\b/g, 'st')
      .replace(/\b(avenue)\b/g, 'ave')
      .replace(/\b(road)\b/g, 'rd')
      .replace(/\b(drive)\b/g, 'dr')
      .replace(/\b(boulevard)\b/g, 'blvd')
      .replace(/\b(north)\b/g, 'n')
      .replace(/\b(south)\b/g, 's')
      .replace(/\b(east)\b/g, 'e')
      .replace(/\b(west)\b/g, 'w')
      .replace(/\s+/g, ' ')
      .trim();

  const citySlug = (value) =>
    normalize(value)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const aliasedCityName = (city) => {
    const normalized = normalize(city);
    if (normalized === 'north york') return 'Toronto';
    return city || '';
  };

  const placeKeyFor = (stockist) => {
    const address = normalizeStreet(stockist.address);
    if (!address) return `id:${stockist.id || stockist.name}`;
    return [address, normalizeStreet(aliasedCityName(stockist.city))].join('|');
  };

  const uniqueStockistsFrom = (stockists) => {
    const uniqueById = new Map();
    stockists.forEach((stockist) => {
      const idKey =
        stockist.id ||
        [stockist.name, stockist.address, stockist.city, stockist.state, stockist.zip].join('|');
      if (!stockist.name || uniqueById.has(idKey)) return;
      uniqueById.set(idKey, stockist);
    });

    const uniqueByPlace = new Map();
    uniqueById.forEach((stockist) => {
      const placeKey = placeKeyFor(stockist);
      const existing = uniqueByPlace.get(placeKey);
      if (!existing || String(stockist.name).length < String(existing.name).length) {
        uniqueByPlace.set(placeKey, stockist);
      }
    });
    return Array.from(uniqueByPlace.values());
  };

  const storeOrigin = () => {
    try {
      return new URL(context.stockists_page_url || 'https://waisa.ca/pages/find-waisa').origin;
    } catch {
      return 'https://waisa.ca';
    }
  };

  const cityPageUrlFor = (city, province, disambiguate) => {
    const slug = citySlug(aliasedCityName(city));
    if (!slug) return context.stockists_page_url;
    const handle =
      disambiguate && province ? `find-waisa-${slug}-${citySlug(province)}` : `find-waisa-${slug}`;
    return `${storeOrigin()}/pages/${handle}`;
  };

  const directionsUrlFor = (stockist) => {
    const destination = [
      stockist.address,
      stockist.city,
      [stockist.province || stockist.state, stockist.postal_code || stockist.zip]
        .filter(Boolean)
        .join(' '),
      stockist.country || 'Canada',
    ]
      .filter(Boolean)
      .join(', ');
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
  };

  const cityProvincesFrom = (stockists) => {
    const provincesByCity = new Map();
    stockists.forEach((stockist) => {
      const city = normalize(aliasedCityName(stockist.city));
      const province = provinceCodeFrom(stockist.state) || normalize(stockist.state);
      if (!city) return;
      const provinces = provincesByCity.get(city) || new Set();
      if (province) provinces.add(province);
      provincesByCity.set(city, provinces);
    });
    return provincesByCity;
  };

  const cityNeedsProvince = (city, provincesByCity) =>
    (provincesByCity.get(normalize(aliasedCityName(city))) || new Set()).size > 1;

  const toCoordinate = (value) => {
    if (value === '' || value == null) return null;
    const coordinate = Number(value);
    return Number.isFinite(coordinate) ? coordinate : null;
  };

  const distanceKm = (latitudeA, longitudeA, latitudeB, longitudeB) => {
    const radians = Math.PI / 180;
    const latitudeDelta = (latitudeB - latitudeA) * radians;
    const longitudeDelta = (longitudeB - longitudeA) * radians;
    const haversine =
      Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(latitudeA * radians) *
        Math.cos(latitudeB * radians) *
        Math.sin(longitudeDelta / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  };

  let stockistsPromise;

  const loadStockists = async (signal) => {
    if (!stockistsPromise) {
      stockistsPromise = fetch(context.stockists_url, {
        headers: { Accept: 'text/html' },
        signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Stockist page returned ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const page = new DOMParser().parseFromString(html, 'text/html');
          const dataElement = page.querySelector('[data-waisa-stockists]');
          if (!dataElement) throw new Error('Stockist data is unavailable');
          return JSON.parse(dataElement.textContent);
        })
        .catch((error) => {
          stockistsPromise = undefined;
          throw error;
        });
    }
    return stockistsPromise;
  };

  const findStockists = async (input = {}, client = {}) => {
    const stockists = uniqueStockistsFrom(await loadStockists(client.signal));
    const parsedQuery = parseLocationQuery(normalize(input.location));
    const productQuery = normalize(input.product);
    const latitude = toCoordinate(input.latitude);
    const longitude = toCoordinate(input.longitude);
    const hasCoordinates = latitude != null && longitude != null;
    const limit = Math.min(Math.max(Number(input.limit) || 5, 1), 20);
    const catalog = retailCans();
    const unknownProduct =
      Boolean(productQuery) &&
      !catalog.some((product) => normalize(product.title).includes(productQuery));
    const cityNames = new Set(
      stockists.flatMap((stockist) => {
        const city = normalize(stockist.city);
        const aliased = normalize(aliasedCityName(stockist.city));
        return [city, aliased].filter(Boolean);
      })
    );

    if (unknownProduct) {
      return {
        source_url: context.stockists_page_url,
        total_matches: 0,
        stockists: [],
        message:
          'No matching stockists were found. Ready-to-drink stockists carry Vibrant Peach, Golden Pineapple, and Wildberry Mint.',
      };
    }

    const provincesByCity = cityProvincesFrom(stockists);
    let located = stockists.filter((stockist) =>
      stockistMatchesLocation(stockist, parsedQuery, cityNames)
    );
    let postalArea;
    if (located.length === 0 && parsedQuery?.postal) {
      for (const area of widerPostalAreas(parsedQuery.postal)) {
        const widerQuery = { ...parsedQuery, postal: { full: null, fsa: area } };
        located = stockists.filter((stockist) =>
          stockistMatchesLocation(stockist, widerQuery, cityNames)
        );
        if (located.length > 0) {
          postalArea = area.toUpperCase();
          break;
        }
      }
    }
    const matches = located
      .map((stockist) => {
        const stockistLatitude = toCoordinate(stockist.lat);
        const stockistLongitude = toCoordinate(stockist.lng);
        const distance =
          hasCoordinates && stockistLatitude != null && stockistLongitude != null
            ? distanceKm(latitude, longitude, stockistLatitude, stockistLongitude)
            : null;
        const displayCity = aliasedCityName(stockist.city);
        const cityPageUrl = cityPageUrlFor(
          displayCity,
          stockist.state,
          cityNeedsProvince(displayCity, provincesByCity)
        );
        return {
          name: stockist.name,
          address: stockist.address,
          city: stockist.city,
          province: stockist.state,
          postal_code: stockist.zip,
          country: stockist.country || 'Canada',
          phone: stockist.phone || null,
          website: stockist.website || null,
          products: catalog,
          distance_km: distance == null ? null : Math.round(distance * 10) / 10,
          directions_url: directionsUrlFor({
            ...stockist,
            province: stockist.state,
            postal_code: stockist.zip,
          }),
          city_page_url: cityPageUrl,
        };
      })
      .sort((stockistA, stockistB) => {
        if (stockistA.distance_km == null && stockistB.distance_km == null) {
          return stockistA.name.localeCompare(stockistB.name);
        }
        if (stockistA.distance_km == null) return 1;
        if (stockistB.distance_km == null) return -1;
        return stockistA.distance_km - stockistB.distance_km;
      });

    const sample = matches[0];
    const cityPageUrl =
      sample && matches.every((stockist) => stockist.city_page_url === sample.city_page_url)
        ? sample.city_page_url
        : undefined;
    const listed = matches.slice(0, limit);
    const fullListUrl = cityPageUrl || context.stockists_page_url;
    let message;
    if (matches.length === 0) {
      const provinces = Array.from(
        new Set(
          stockists
            .map((stockist) => provinceCodeFrom(stockist.state))
            .filter(Boolean)
            .map((code) => code.toUpperCase())
        )
      ).sort();
      message = `No matching stockists were found. Waísa stockists are in ${provinces.join(', ')}. For a place outside those provinces, call this tool again with latitude and longitude to get the nearest stores, or send the user to ${context.stockists_page_url}.`;
    } else if (postalArea) {
      message = `No stockist lists postal code ${formatPostal(parsedQuery.postal.full || parsedQuery.postal.fsa)}. These stores are in the wider ${postalArea} postal area. For the closest store, call this tool again with latitude and longitude, or send the user to ${fullListUrl}.`;
    } else if (matches.length > listed.length) {
      message = `Showing ${listed.length} of ${matches.length} matching stockists. Open ${fullListUrl} for the full list, named retailers, and directions.`;
    } else if (cityPageUrl && parsedQuery?.place) {
      message = `Send the user to ${cityPageUrl} for the full ${aliasedCityName(sample.city)} list and directions.`;
    }

    return {
      source_url: context.stockists_page_url,
      city_page_url: parsedQuery?.place ? cityPageUrl : undefined,
      total_matches: matches.length,
      postal_area: postalArea,
      stockists: listed,
      message,
    };
  };

  const learnAboutGuayusa = async ({ topic = 'all' } = {}) => {
    const validTopics = ['overview', 'energy', 'compounds', 'tradition', 'preparation'];
    const selectedTopics = topic === 'all' ? validTopics : [topic];
    const facts = Object.fromEntries(
      selectedTopics
        .filter((selectedTopic) => validTopics.includes(selectedTopic))
        .map((selectedTopic) => [selectedTopic, context.guayusa[selectedTopic]])
    );

    return {
      source_url: context.guayusa_page_url,
      research_sources: context.research_sources,
      topic,
      facts,
      note: 'This is general product education, not medical advice. Caffeine effects vary by person.',
    };
  };

  const tools = [
    {
      name: 'find_waisa_stockists',
      description:
        'Find Canadian retailers and cafés that stock Waísa guayusa. Every stockist carries the full ready-to-drink can lineup (Vibrant Peach, Golden Pineapple, Wildberry Mint). Search by city, province name or code (Ontario, ON), postal code with or without a space, store name, flavour, or rank nearby stores by latitude and longitude. Returns named stores, addresses, directions URLs, and the matching city page so a Canadian user can walk in.',
      inputSchema: {
        type: 'object',
        properties: {
          location: {
            type: 'string',
            description:
              'A Canadian city, province (Ontario or ON), postal code (V6B 1A1 or V6B1A1), "City, ON", store name, or street.',
          },
          product: {
            type: 'string',
            description:
              'Optional flavour name. Every stockist carries Vibrant Peach, Golden Pineapple, and Wildberry Mint.',
          },
          latitude: {
            type: 'number',
            minimum: -90,
            maximum: 90,
            description: 'Latitude used with longitude to rank stores by distance.',
          },
          longitude: {
            type: 'number',
            minimum: -180,
            maximum: 180,
            description: 'Longitude used with latitude to rank stores by distance.',
          },
          limit: {
            type: 'integer',
            minimum: 1,
            maximum: 20,
            description: 'Maximum number of stockists to return. Defaults to 5.',
          },
        },
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, untrustedContentHint: false },
      execute: findStockists,
    },
    {
      name: 'learn_about_guayusa_energy',
      description:
        'Return facts about guayusa: what it is, why its naturally caffeinated energy can feel different, key whole-leaf compounds, Kichwa tradition, and preparation methods. Read-only product education; does not navigate or change the page.',
      inputSchema: {
        type: 'object',
        properties: {
          topic: {
            type: 'string',
            enum: ['overview', 'energy', 'compounds', 'tradition', 'preparation', 'all'],
            description: 'The guayusa topic to return. Defaults to all.',
          },
        },
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, untrustedContentHint: false },
      execute: learnAboutGuayusa,
    },
  ];

  Promise.allSettled(tools.map((tool) => modelContext.registerTool(tool)));
})();
